import { useCallback, useRef, useState, type ReactNode } from 'react'
import { MerchantPickerDialog } from '../merchants/picker'
import type { Txn } from './transactionWire'
import { usePickedRowFocus } from './usePickedRowFocus'
import { useQuickMerchant } from './useQuickMerchant'

/**
 * One merchant picker per screen, opened from any row's merchant pill (Transactions, Cashflow detail) —
 * `useCategoryQuickPick`, for merchants. The pick goes through `useQuickMerchant`, which PATCHes the row
 * and invalidates both the transactions and cashflow caches; nothing is removed locally — on a scoped
 * page (a merchant's, or "No merchant") the row leaves when the refetch says so, and `usePickedRowFocus`
 * then recovers focus.
 *
 * The dialog's header carries the bank's raw description: that is what a user matches a merchant against.
 * "Remove merchant" is offered on a row that has one, and sends `null`.
 */
export function useMerchantQuickPick(): { onMerchantClick: (t: Txn, opener: HTMLButtonElement) => void; dialog: ReactNode } {
  const [txn, setTxn] = useState<Txn | null>(null)
  const opener = useRef<HTMLElement | null>(null)
  const watch = usePickedRowFocus()
  const quick = useQuickMerchant()

  const onMerchantClick = useCallback((t: Txn, el: HTMLButtonElement) => {
    opener.current = el
    setTxn(t)
  }, [])
  const dialog = (
    <MerchantPickerDialog
      open={txn !== null}
      onClose={() => setTxn(null)}
      value={txn?.merchantId ?? null}
      onPick={(id, merchant, extras) => {
        if (txn === null) return
        // The picker hands over the merchant with every real pick; an id alone is still that id, never a removal.
        const next = id === null ? null : (merchant ?? { id, name: 'the new merchant', identity: { id, name: 'the new merchant', type: 'business', is_global: false } })
        watch(opener.current, quick.set(txn, next, extras))
      }}
      context={
        txn === null ? undefined : { merchant: txn.merchant, date: txn.date, amountCents: txn.amountCents, currency: txn.currency, raw: txn.raw }
      }
      allowNone
      offerCategory={txn !== null && txn.categoryId === null}
      restoreFocusTo={opener}
    />
  )
  return { onMerchantClick, dialog }
}
